import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, Tag as TagIcon, ArrowRight, Search } from 'lucide-react';
import MarketingLayout from '@/components/marketing/MarketingLayout';
import Seo, { SITE } from '@/components/shared/Seo';
import blogAPI from '@/services/blogAPI';

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; }
}

function PostCard({ post, featured = false }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      className={`group card card-hover soft rounded-[20px] md:rounded-[24px] overflow-hidden flex ${featured ? 'md:flex-row md:col-span-2' : 'flex-col'}`}
      data-testid={`blog-card-${post.slug}`}
    >
      {post.cover_image ? (
        <div className={`overflow-hidden ${featured ? 'md:w-1/2 aspect-[16/10] md:aspect-auto' : 'aspect-[16/9]'}`}>
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
        </div>
      ) : (
        <div className={`aurora ${featured ? 'md:w-1/2 aspect-[16/10] md:aspect-auto' : 'aspect-[16/9]'}`} />
      )}
      <div className={`p-5 md:p-6 flex flex-col ${featured ? 'md:flex-1' : ''}`}>
        <div className="flex items-center gap-3 text-[11.5px] md:text-[12px] t3">
          <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmtDate(post.published_at || post.created_at)}</span>
          {(post.tags || []).slice(0, 2).map((t) => (
            <span key={t} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-[#6D5EF7]/10 text-[#6D5EF7] text-[10.5px] font-medium">#{t}</span>
          ))}
        </div>
        <h3 className={`dis mt-3 font-semibold t1 ${featured ? 'text-[22px] md:text-[28px]' : 'text-[17px] md:text-[19px]'}`} style={{ fontFamily: "'Sora', sans-serif" }}>{post.title}</h3>
        {post.subtitle && <p className="mt-2 text-[13.5px] md:text-[14px] t3 leading-[21px] md:leading-[22px] line-clamp-2">{post.subtitle}</p>}
        <span className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#6D5EF7]">Read more <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" /></span>
      </div>
    </Link>
  );
}

export default function BlogList() {
  const [params, setParams] = useSearchParams();
  const tag = params.get('tag') || '';
  const search = params.get('search') || '';
  const [data, setData] = useState({ posts: [], tags: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(search);

  useEffect(() => {
    setLoading(true);
    blogAPI.listPosts({ tag, search }).then(setData).catch(() => setData({ posts: [], tags: [], total: 0 })).finally(() => setLoading(false));
  }, [tag, search]);

  const onSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set('search', q);
    if (tag) p.set('tag', tag);
    setParams(p);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Numuv Blog',
    url: `${SITE}/blog`,
    blogPost: (data.posts || []).slice(0, 10).map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      url: `${SITE}/blog/${p.slug}`,
      datePublished: p.published_at,
    })),
  };

  return (
    <MarketingLayout>
      {() => (
        <>
          <Seo
            title="Blog — AI recruitment insights, guides & product updates"
            description="The Numuv blog: guides, product updates, and deep dives on AI recruitment, hiring workflows, and candidate experience."
            path="/blog"
            keywords="AI recruitment blog, hiring insights, candidate experience, recruitment automation, HR tech blog"
            jsonLd={jsonLd}
          />
          <section className="mx-auto max-w-[1240px] px-4 md:px-6 pt-14 md:pt-20 pb-8">
            <div className="text-center">
              <span className="text-[11.5px] md:text-[12px] font-semibold uppercase tracking-[0.18em] text-[#6D5EF7]">The Numuv Blog</span>
              <h1 className="dis mt-3 text-[34px] md:text-[52px] font-bold tracking-[-0.03em] t1" style={{ fontFamily: "'Sora', sans-serif" }}>Ideas that move hiring forward</h1>
              <p className="mx-auto mt-4 max-w-[620px] text-[14.5px] md:text-[17px] leading-[24px] md:leading-[27px] t2">Playbooks, product updates, and honest takes on where AI is (and isn&apos;t) helping teams hire better.</p>
            </div>

            <form onSubmit={onSearch} className="mt-8 md:mt-10 max-w-[540px] mx-auto flex items-center gap-2 rounded-full card thm px-4 py-2 soft" data-testid="blog-search-form">
              <Search className="w-4 h-4 t3" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles…" className="flex-1 bg-transparent outline-none text-[14px] t1 placeholder:text-[color:var(--t4)]" data-testid="blog-search-input" />
              <button type="submit" className="aurora-btn rounded-full px-4 py-1.5 text-[12.5px] font-semibold text-white">Search</button>
            </form>

            {(data.tags || []).length > 0 && (
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <Link to="/blog" className={`inline-flex items-center gap-1.5 rounded-full border brd px-3 py-1.5 text-[12px] transition ${!tag ? 'bg-[#6D5EF7] text-white border-transparent' : 't2 hover:text-[#6D5EF7]'}`}>All</Link>
                {(data.tags || []).map((t) => (
                  <Link key={t.tag} to={`/blog?tag=${encodeURIComponent(t.tag)}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border brd px-3 py-1.5 text-[12px] transition ${tag === t.tag ? 'bg-[#6D5EF7] text-white border-transparent' : 't2 hover:text-[#6D5EF7]'}`}
                  >
                    <TagIcon className="w-3 h-3" /> {t.tag} <span className="opacity-70">({t.count})</span>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section className="mx-auto max-w-[1240px] px-4 md:px-6 pb-16 md:pb-24">
            {loading ? (
              <p className="text-center t3 py-12">Loading posts…</p>
            ) : data.posts.length === 0 ? (
              <div className="text-center py-16">
                <p className="dis text-[22px] font-semibold t1" style={{ fontFamily: "'Sora', sans-serif" }}>No posts yet</p>
                <p className="mt-2 t3 text-[14px]">Our team is drafting the first stories. Come back soon, or subscribe by emailing <a href="mailto:hello@numuv.app" className="underline">hello@numuv.app</a>.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7" data-testid="blog-grid">
                {data.posts.map((p, idx) => (
                  <PostCard key={p.id} post={p} featured={idx === 0 && !tag && !search && data.posts.length > 3} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </MarketingLayout>
  );
}
